import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import Markdown from "src/components/Markdown";
import { useTranslation } from "react-i18next";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

const spacingClassPattern = /\b(p|px|py|pt|pb|pl|pr)-/;

const TitleSection = ({
  title,
  text,
  as: Tag = "h1",
  dark = false,
  className,
  borderColor,
  textFontSize,
  backgroundColor,
  bgImage,
}) => {
  const { t } = useTranslation();
  const bg = bgImage?.src ? bgImage : null;
  const responsiveBgImage = useResponsiveImageUrl(bg?.src || "");
  const resolvedBgImage = bg ? responsiveBgImage : null;
  const hasSpacingClass = spacingClassPattern.test(className || "");
  const pinToBottom = bg?.pin === "bottom";
  const sectionBgImage = pinToBottom ? null : resolvedBgImage;

  return (
    <Section
      dark={dark}
      className={`title-section ${className || ""}`}
      borderColor={borderColor}
      textFontSize={textFontSize}
      backgroundColor={backgroundColor}
      $bgImage={sectionBgImage}
      $bgPosition={bg?.position}
      $bgSize={bg?.size}
      $hideBgOnMobile={Boolean(bg?.hideOnMobile)}
      $stackBgOnMobile={Boolean(bg?.stackOnMobile)}
      $hasSpacingClass={hasSpacingClass}
      $pinToBottom={pinToBottom}
    >
      {pinToBottom && resolvedBgImage && (
        <PinnedBgImage
          src={resolvedBgImage}
          alt=""
          $size={bg.size}
          $clipBottom={bg.clipBottom || "22%"}
          $hideOnMobile={Boolean(bg.hideOnMobile || bg.stackOnMobile)}
        />
      )}
      <Tag>{t(title)}</Tag>
      <span>{""}</span>
      <Markdown>{text}</Markdown>
      {bg?.stackOnMobile && resolvedBgImage && (
        <MobileStackImage src={resolvedBgImage} alt={bg.alt || ""} />
      )}
    </Section>
  );
};

TitleSection.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string,
  dark: PropTypes.bool,
  borderColor: PropTypes.string,
  textFontSize: PropTypes.string,
  backgroundColor: PropTypes.string,
  bgImage: PropTypes.shape({
    src: PropTypes.string,
    position: PropTypes.string,
    size: PropTypes.string,
    hideOnMobile: PropTypes.bool,
    stackOnMobile: PropTypes.bool,
    pin: PropTypes.oneOf(["bottom"]),
    clipBottom: PropTypes.string,
    alt: PropTypes.string,
  }),
};

const Section = styled.div`
  ${(props) => !props.$hasSpacingClass && `padding: 40px 0;`}
  height: fit-content;
  width: 100%;
  color: var(--white);
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  letter-spacing: 1px;
  text-align: center;
  color: ${(props) => (props.dark ? "var(--black)" : "var(--white)")};
  background-color: ${(props) => props.backgroundColor || "transparent"};
  ${(props) =>
    (props.$bgImage || props.$pinToBottom) &&
    `
    position: relative;
    overflow: hidden;
  `}
  ${(props) =>
    props.$bgImage &&
    `
    background-image: url(${props.$bgImage});
    background-position: ${props.$bgPosition || "center"};
    background-size: ${props.$bgSize || "cover"};
    background-repeat: no-repeat;
  `}
  ${(props) =>
    props.$pinToBottom &&
    `
    > :not(img) {
      position: relative;
      z-index: 1;
    }
  `}

  & > h1, & > h2 {
    margin: 0;
    font-weight: 900;
    margin-bottom: 10px;
    font-size: 45px;
    line-height: 1.2;
  }
  & > h3 {
    margin: 0;
    font-weight: 900;
    margin-bottom: 10px;
    font-size: 35px;
    line-height: 1.2;
  }
  & > span {
    width: 100%;
    max-width: 275px;
    border-bottom: 2px solid ${(props) => (props.borderColor ? props.borderColor : "var(--red)")};
    margin-bottom: 20px;
  }
  & > p {
    width: 100%;
    max-width: 800px;
    text-align: center;
    /* font-weight: 200; */
    font-size: ${(props) => props.textFontSize || "1.2rem"};
    padding: 0;
  }
  @media screen and (max-width: 991px) {
    ${(props) => !props.$hasSpacingClass && `padding: 20px 0;`}
    ${(props) =>
      (props.$stackBgOnMobile || props.$hideBgOnMobile) &&
      `
      background-image: none;
    `}
    & > h1, & > h2 {
      text-align: center;
      font-size: 32px;
      padding: 0 20px;
    }
    & > h3 {
      text-align: center;
      font-size: 26px;
      padding: 0 20px;
    }
    & > p {
      padding: 0 20px;
    }
  }
`;

const PinnedBgImage = styled.img`
  display: none;

  @media screen and (min-width: 992px) {
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    width: ${(props) => props.$size || "28%"};
    height: auto;
    transform: translateY(${(props) => props.$clipBottom || "22%"});
    pointer-events: none;
    z-index: 0;
  }

  ${(props) =>
    props.$hideOnMobile &&
    `
    @media screen and (max-width: 991px) {
      display: none;
    }
  `}
`;

const MobileStackImage = styled.img`
  display: none;
  width: min(280px, 70%);
  height: auto;
  margin: 16px auto 0;
  object-fit: contain;

  @media screen and (max-width: 991px) {
    display: block;
  }
`;

export default TitleSection;
